import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { ACCESS_TOKEN, REDIRECT_AFTER_LOGIN, REFRESH_TOKEN, StorageSetItem, USER } from '../../lib/storage'
import { sameOriginPath } from '../../lib/safeRedirect'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { setSentryUser } from '../../lib/sentryUser'
import { noticeFor } from '../../lib/signInNotices'

const SIGN_IN_FAILED = 'Failed to sign in'

export const useSignInForm = ({ thunk, defaultRedirect }) => {
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const notice = noticeFor(searchParams.get('notice'))
    if (!notice) return

    showSnackBar(notice.severity, notice.text)
    const rest = new URLSearchParams(searchParams)
    rest.delete('notice')
    setSearchParams(rest, { replace: true })
  }, [searchParams, setSearchParams])

  const completeSignIn = async ({ accessToken, refreshToken, user }) => {
    await StorageSetItem(ACCESS_TOKEN, accessToken)
    await StorageSetItem(REFRESH_TOKEN, refreshToken)
    await StorageSetItem(USER, user)
    setSentryUser(user)

    const redirectPath = sameOriginPath(localStorage.getItem(REDIRECT_AFTER_LOGIN))
    localStorage.removeItem(REDIRECT_AFTER_LOGIN)
    if (redirectPath) {
      window.location.href = redirectPath
    } else {
      defaultRedirect()
    }
  }

  const submit = async () => {
    if (submitting) return
    setSubmitting(true)
    const response = await dispatch(thunk({ email, password }))

    const session = thunk.fulfilled.match(response) ? response.payload?.data : null
    if (session) {
      await completeSignIn(session)
      return
    }

    setSubmitting(false)
    const message = typeof response.payload === 'string' && response.payload ? response.payload : SIGN_IN_FAILED
    showSnackBar('error', message)
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword: () => setShowPassword((shown) => !shown),
    submitting,
    submit,
  }
}
